import { IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPLabel, IPText } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import HrefLink from 'components/Links/HrefLink';
import EntityPicker from 'components/Pickers/EntityPicker';
import BaseTableList from 'components/TableList/BaseTableList';
import { getEntity, getEntityName } from 'globalFunctions';
import { deleteIcon, globalStackTokensGapMedium } from 'globalStyles';
import { TFunction } from 'i18next';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import Control from 'models/control';
import Entity, { EntityTypes } from 'models/entity';
import KPI from 'models/kpi/kpi';
import Objective from 'models/objective/objective';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiAddKPIToControl, apiGetControls, apiRemoveKPIFromControl } from 'services/Api/controlService';
import { apiGetKPIContext } from 'services/Api/kpiService';
import { apiAddKPIToObjective, apiGetObjectives, apiRemoveKPIFromObjective } from 'services/Api/objectiveService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';
import { sortOnEntity } from 'utils/sorting';

interface IKPIContextProps {
  kpi?: KPI;
  readOnly?: boolean;
}

export const KPIContext = (props: IKPIContextProps) => {
  const { t } = useTranslation(['kpi', 'translation', 'theme', 'control', 'risk']);
  const appContext = useContext(AppContext);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [isLoadingPicker, setIsLoadingPicker] = useState<boolean>(false);
  const [allObjectives, setAllObjectives] = useState<Objective[]>([]);
  const [allControls, setAllControls] = useState<Control[]>([]);
  const [allEntities, setAllEntities] = useState<Entity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBusyAddingRemoving, setIsBusyAddingRemoving] = useState<number>(0);
  const [canUpdate] = useState<boolean>(
    !props.readOnly &&
      (props.kpi ? hasUserDataPermission(appContext, [props.kpi.authSchemaId], AuthSchemaLineOperation.Update) : false),
  );

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.kpi]);

  const loadData = async () => {
    if (isLoading || !props.kpi) return;

    try {
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiGetKPIContext(props.kpi.kpiId, accessToken, appContext.globalDataCache);

      const entities: Entity[] = [];

      result.objectives.forEach((objective) => {
        entities.push(getEntity(objective));
      });

      result.controls.forEach((control) => {
        entities.push(getEntity(control));
      });

      setEntities(entities.sort((a, b) => sortOnEntity(a, b)));
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDataForPicker = async () => {
    if (isLoadingPicker) return;

    try {
      setIsLoadingPicker(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const _allObjectivesPromise = apiGetObjectives(accessToken, appContext.globalDataCache);
      const _allControlsPromise = apiGetControls(accessToken, appContext.globalDataCache);

      const [_allObjectivesData, _allControlsData] = await Promise.allSettled([
        _allObjectivesPromise,
        _allControlsPromise,
      ]);

      const _allEntities: Entity[] = [];
      let errorMessage = '';

      if (_allObjectivesData.status === 'fulfilled') {
        _allObjectivesData.value.forEach((_objective: Objective) => {
          _allEntities.push(getEntity(_objective));
        });
        setAllObjectives(_allObjectivesData.value);
      } else {
        errorMessage += `${t('theme:Title')} : ${_allObjectivesData.reason}`;
      }

      if (_allControlsData.status === 'fulfilled') {
        _allControlsData.value.forEach((_control: Control) => {
          _allEntities.push(getEntity(_control));
        });
        setAllControls(_allControlsData.value);
      } else {
        errorMessage += `${t('theme:Title')} : ${_allControlsData.reason}`;
      }

      if (errorMessage) appContext.setError(errorMessage);

      setAllEntities(_allEntities);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoadingPicker(false);
    }
  };

  const onRenderCol = (item: Entity, column: IColumn): JSX.Element | null => {
    if (!item) return null;

    switch (column.key) {
      case 'type':
        return <IPText>{getEntityName(item, t as TFunction<string[]>)}</IPText>;
      case 'name':
        return (
          <IPText>
            <HrefLink entity={item}>{item.entityName}</HrefLink>
          </IPText>
        );
      case 'remove':
        if (!canUpdate) return null;

        return (
          <IPIconButton
            disabled={isBusyAddingRemoving !== 0}
            iconProps={deleteIcon}
            onClick={() => {
              if (!item) return;

              onRemove(item);
            }}
            styles={{ root: { height: 18 } }}
          />
        );
      default:
        return null;
    }
  };

  const getColumns = (): IColumn[] => {
    const all = [
      {
        key: 'type',
        name: t('kpi:Context.List.Type'),
        minWidth: 100,
        maxWidth: 100,
      },
      {
        key: 'name',
        name: t('kpi:Context.List.Name'),
        isMultiline: true,
        isResizable: true,
        minWidth: 100,
      },
      {
        key: 'remove',
        name: t('kpi:Context.List.Remove'),
        minWidth: 100,
        maxWidth: 100,
      },
    ];

    if (!canUpdate) {
      return all.filter((c) => c.key !== 'remove');
    }

    return all;
  };

  const [columns, setColumns] = useState<IColumn[]>(getColumns());

  const onRemove = async (entity: Entity) => {
    try {
      if (isBusyAddingRemoving || !props.kpi) return;
      setIsBusyAddingRemoving(entity.entityId);

      const removed = await removeKPIFromEntity(props.kpi.kpiId, entity.entityId, entity.typeOfEntity);
      if (removed) {
        setEntities(entities.filter((c) => c.typeOfEntity !== entity.typeOfEntity || c.entityId !== entity.entityId));
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsBusyAddingRemoving(0);
    }
  };

  const filterEntities = (all: Entity[], related: Entity[]): Entity[] => {
    return all.filter((e) => {
      const foundKPI = related.find((r) => {
        return r.entityId === e.entityId && r.typeOfEntity === e.typeOfEntity;
      });
      if (foundKPI) return false;

      return true;
    });
  };

  const addEntity = async (entity: Entity) => {
    try {
      if (isBusyAddingRemoving || !props.kpi) return;
      setIsBusyAddingRemoving(entity.entityId);
      let validAdd = false;
      switch (entity.typeOfEntity) {
        case EntityTypes.Objective: {
          validAdd = allObjectives.find((c) => c.objectiveId === entity.entityId) !== undefined;
          break;
        }
        case EntityTypes.Control: {
          validAdd = allControls.find((c) => c.controlId === entity.entityId) !== undefined;
          break;
        }
        default:
          break;
      }

      if (validAdd) {
        const added = await addKPIToEntity(props.kpi.kpiId, entity.entityId, entity.typeOfEntity);
        if (added) setEntities([...entities, entity]);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsBusyAddingRemoving(0);
    }
  };

  const addKPIToEntity = async (kpiId: number, toId: number, toType: EntityTypes): Promise<boolean> => {
    if (isLoading || !props.kpi) return false;

    let output = false;
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      switch (toType) {
        case EntityTypes.Objective: {
          output = await apiAddKPIToObjective(toId, kpiId, accessToken);
          break;
        }
        case EntityTypes.Control: {
          output = await apiAddKPIToControl(kpiId, toId, accessToken);
          break;
        }
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }

    return output;
  };

  const removeKPIFromEntity = async (kpiId: number, toId: number, toType: EntityTypes): Promise<boolean> => {
    if (isLoading || !props.kpi) return false;

    let output = false;
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      switch (toType) {
        case EntityTypes.Objective: {
          output = await apiRemoveKPIFromObjective(toId, kpiId, accessToken);
          break;
        }
        case EntityTypes.Control: {
          output = await apiRemoveKPIFromControl(kpiId, toId, accessToken);
          break;
        }
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }

    return output;
  };

  //
  // Main render
  //

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPLabel> {t('kpi:Context.Relations')}</IPLabel>
      </IPStack.Item>
      <IPStack.Item grow>
        <BaseTableList
          compact
          noTopPadding
          isLoading={isLoading}
          items={entities}
          columns={columns}
          selectionMode={IPSelectionMode.none}
          layoutMode={IPDetailsListLayoutMode.justified}
          onRenderCol={onRenderCol}
          setColumns={setColumns}
          disableSort={true}
        />
      </IPStack.Item>
      {canUpdate && (
        <IPStack.Item>
          <IPStack>
            <IPLabel>{t('kpi:Context.AddText')}</IPLabel>
            <IPStack.Item>
              <EntityPicker
                entities={filterEntities(allEntities, entities)}
                addSelectedEntity={addEntity}
                loadData={loadDataForPicker}
                isLoading={isLoadingPicker}
                isBusyAdding={isBusyAddingRemoving}
                isOnPanel={appContext.isMobileView}
              />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
